import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip
} from "@mui/material";
import {useActivateMutation, useTransferMutation, useUpgradeMutation} from "../../../core/reducers/campaign";
import Loading from "../../Loading";
import CampaignFrame from "./CampaignFrame";
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {Alert, Autocomplete, LoadingButton} from "@mui/lab";
import {useGetAllEnvironmentsQuery} from "../../../core/reducers/environments";
import SyncPublisherProgress from "./SyncPublisherProgress";
import {useLocation, useSearchParams} from "react-router-dom";
import {formatInTimeZone} from "date-fns-tz";
import {useDispatch, useSelector} from "react-redux";
import Diff from "../Diff";
import {
    DEFAULT_ENV,
    DEFAULT_ENV2,
    getEnvName,
    isAdmin,
    isProdAdmin,
    PROD
} from "../../Utils/CommonUtils";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MultipleSelect from "../MultipleSelect";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {useNavigate} from "react-router";
import TransferResultDialog from "../Transfer/TransferResultDialog";
import {useGetAllCampaignTypesQuery} from "../../../core/reducers/campaignType";
import PublishDiff from "../../Campaign/PublishDiff";
import {useGetEventsQuery} from "../../../core/reducers/calendar";
import ConfirmMassActionDialog from "../../ConfirmMassActionDialog";
import AudienceBulkUpdateDialog from "../../Campaign/AudienceBulkUpdateDialog";
import {getBundleUpdateCampaigns} from "../../../core/transfer/bundleUpdate";
import BundleUpdateConfirmDialog from "../Transfer/BundleUpdateConfirmDialog";
import {
    createPublisherReturnState,
    getInitialCampaignFilters
} from "./CampaignFilterState";


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export default function PublisherCampaigns() {
    const statusItems = ['ACTIVE', 'SUSPENDED', 'HIDDEN', 'DEACTIVATED']
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const sourceMode = useSelector((state) => state.data.SourceMode);
    const dateState = useSelector((state) => state.data.DateFilter);
    const initialFiltersRef = useRef(null);
    if (initialFiltersRef.current === null) {
        initialFiltersRef.current = getInitialCampaignFilters({
            searchParams,
            storage: window.localStorage,
            defaultFrom: Math.round(new Date(dateState.from).getTime() / 1000),
            defaultTill: Math.round(new Date(dateState.till).getTime() / 1000)
        });
    }
    const initialFilters = initialFiltersRef.current;
    const [env1, setEnv1] = useState(getEnvName('env', sourceMode, searchParams))
    const [env2, setEnv2] = useState(getEnvName('env2', sourceMode, searchParams))

    const [filterText, setFilterText] = useState(initialFilters.filterText)


    const [filterType, setFilterType] = useState(initialFilters.filterType)

    const [filterCampaignType, setFilterCampaignType] = useState(initialFilters.filterCampaignType)

    const [filterStatusesSelected, setFilterStatusesSelected] = useState(initialFilters.filterStatuses)

    const [dateFilter, setDateFilter] = useState({
        from: (() => {
            try {
                return new Date(initialFilters.from * 1000);
            } catch (error) {
                console.warn('Invalid "from" date parameter:', error);
                return new Date(dateState.from);
            }
        })(),
        till: (() => {
            try {
                return new Date(initialFilters.till * 1000);
            } catch (error) {
                console.warn('Invalid "till" date parameter:', error);
                return new Date(dateState.till);
            }
        })()
    });

    const [calendarMode, setCalendarMode] = useState({
        env: '',
        active: false
    })

    const [showConfirmTransfer, setShowConfirmTransfer] = useState(false)
    const [showConfirmActivate, setShowConfirmActivate] = useState(false)
    const [showConfirmUpgrade, setShowConfirmUpgrade] = useState(false)
    const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);

    const [confirmErrorsDialog, setConfirmErrorsDialog] = useState(false)
    const [confirmBundleDialog, setConfirmBundleDialog] = useState(false)
    const [bundleUpdateInfo, setBundleUpdateInfo] = useState({})
    const [isAllowedSource, setIsAllowedSource] = useState(false)
    const [isProdEnv, setIsProdEnv] = useState(env2 === 'production')
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [errorAfterCheck, setErrorAfterCheck] = useState(false);
    const [transferInProgress, setTransferInProgress] = useState([]);


    const {
        data: types,
        status: typeStatus
    } = useGetAllCampaignTypesQuery({}, {})

    const SHORT_CAMPAIGN_NAMES = types != undefined ? Object.keys(types).reduce((acc, item) => {
            const abbreviation = types[item].metaData?.abbreviation;
            if (abbreviation) {
                acc[item] = abbreviation;
            }
            return acc;
        }, {})
        : {};

    const currentTime = Math.round(new Date(new Date().getFullYear()).setHours(0, 0, 0, 0) / 1000)
    const {
        data: calendarEvents,
        isUninitialized: isUninitializedEvents,
        isSuccess: calendarEventsSuccess
    } = useGetEventsQuery(currentTime);


    // мемоизируем: новый массив на каждый рендер ломал React.memo списков и перерисовывал все карточки
    const externalLinks = useMemo(() => calendarEvents === undefined ? [] : calendarEvents.map(event => ({
        id: event.id,
        eventId: event.eventId,
        asana: event.asanaTaskGid
    })), [calendarEvents])

    const {
        data: environments,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error
    } = useGetAllEnvironmentsQuery();


    if (environments !== undefined && env2 !== 'production') {
        if (!environments.hasOwnProperty(env1) && env1 !== DEFAULT_ENV) setEnv1(DEFAULT_ENV)
        if (!environments.hasOwnProperty(env2) && env2 !== DEFAULT_ENV2) setEnv2(DEFAULT_ENV2)
    }

    const getEnvByName = (environments, name) => {
        if (environments == undefined) return [];
        return Object.entries(environments).filter(env => {
            return env[1].name === name
        })
    }

    const userSession = useSelector((state) => state.data.UserSession);


    const isAllowedForEnv = (sourceEnv, targetEnv) => {

        const envObject = getEnvByName(environments, targetEnv);

        if (typeof envObject != "object") return false;

        if (envObject !== undefined && envObject.length > 0) {
            if (envObject[0][1].allowedSources.length > 0)
                return envObject[0][1].allowedSources.includes(sourceEnv) ? true : false
        } else {
            return false
        }
        return true
    }

    const prevEnv2 = useRef(env2);
    useEffect(() => {

        if (prevEnv2.current != env2) {
            setSelectedList([])
        }

        prevEnv2.current = env2
    }, [env2])


    useEffect(() => {
        console.log(errorAfterCheck)
        setEnv1(getEnvName('env', sourceMode, searchParams));
        setEnv2(getEnvName('env2', sourceMode, searchParams))
        setIsProdEnv(env2 === PROD)
        if (environments && env2 !== 'production') {
            const env = getEnvByName(environments, env2);

            if (env !== undefined && env.length > 0) {
                if (env[0][1].allowedSources.length > 0)
                    env[0][1].allowedSources.includes(env1) ? setIsAllowedSource(true) : setIsAllowedSource(false);
                else
                    setIsAllowedSource(true)
            } else {

            }
        } else {
            setIsAllowedSource(true)
        }

    }, [searchParams, environments, env2, env1]);

    const [completedTransferList, setCompletedTransferList] = useState([]);
    const [openResultDialog, setOpenResultDialog] = useState(false);


    const [cachedItems, setCachedItems] = useState({})
    const [selectedList, setSelectedList] = useState([])
    const [skipBundleUpdateList, setSkipBundleUpdateList] = useState([])

    // стабильные колбэки (функциональные обновления состояния) — не перерисовывают списки кампаний
    const onSelectItem = useCallback((data) => {
        setSelectedList(prev => {
            let array = [...prev]; // make a separate copy of the array
            let index = array.indexOf(data.item.id)
            if (index !== -1) {
                array.splice(index, 1);
            } else {
                array.push(data.item.id)
            }
            return array;
        });

        setCachedItems(prev => ({...prev, [data.item.id]: data}))
    }, [])

    const onSelectMultipleItems = useCallback((itemsData, isSelect) => {
        setSelectedList(prev => {
            let array = [...prev];
            itemsData.forEach(data => {
                let index = array.indexOf(data.item.id);
                if (isSelect && index === -1) {
                    array.push(data.item.id);
                } else if (!isSelect && index !== -1) {
                    array.splice(index, 1);
                }
            });
            return array;
        });

        // данные кампаний, добавляемых в выделение (нужны для transfer / массовых действий)
        setCachedItems(prev => {
            const newCachedItems = {...prev};
            itemsData.forEach(data => {
                if (isSelect) newCachedItems[data.item.id] = data;
            });
            return newCachedItems;
        });
    }, [])


    // Поднимаем из PublishDiffItem признак изменения версии бандла по каждой кампании.
    const handleBundleUpdateInfo = (campaignId, info) => {
        setBundleUpdateInfo(prev => {
            const existing = prev[campaignId]
            if (existing
                && existing.hasBundleUpdate === info.hasBundleUpdate
                && JSON.stringify(existing.generations) === JSON.stringify(info.generations)) {
                return prev
            }
            return {...prev, [campaignId]: info}
        })
    }

    // Кампании из текущего выбора, у которых меняется версия бандла и обновление НЕ пропущено пользователем.
    const getBundleUpdateCampaignsList = () => {
        return getBundleUpdateCampaigns(selectedList, bundleUpdateInfo, skipBundleUpdateList)
    }

    const [
        transfer,
        transferResponse
    ] = useTransferMutation()

    const [activateCampaign, activateCampaignResponse] = useActivateMutation();
    const [upgradeCampaign, upgradeCampaignResponse] = useUpgradeMutation();


    const handleTransfer = async (force, bundleConfirmed) => {
        setShowConfirmTransfer(false)

        if (env1 == 'liveops' && force !== true && selectedList.filter(item => cachedItems[item].item.warnings.length > 0).length > 0) {
            setConfirmErrorsDialog(true)
            return false
        } else {
            setConfirmErrorsDialog(false)
        }

        // Доп. подтверждение: хотя бы у одной кампании меняется версия бандла.
        if (bundleConfirmed !== true && getBundleUpdateCampaignsList().length > 0) {
            setConfirmBundleDialog(true)
            return false
        } else {
            setConfirmBundleDialog(false)
        }

        setOpenResultDialog(true)
        let newCompletedTransferList = [];
        for (let id in selectedList) {
            let campaign = selectedList[id]
            newCompletedTransferList.push({campaign: campaign})
            let data = {
                environment: env2,
                source: env1,
                campaign: campaign,
                env: env2
            }

            if(skipBundleUpdateList.includes(campaign)) {
                data["skipBundleUpdate"] = true
            }

            const resp = await transfer(data)

            console.log('Try transfer ' + campaign + ' from ' + env1 + ' to ' + env2)
            console.log('Response ', resp)
            if (
                (resp.hasOwnProperty('data') && resp.data.hasOwnProperty('transfer_id'))
            ) {
                newCompletedTransferList.filter(i => i.campaign === campaign)[0].transferred = true;
                newCompletedTransferList.filter(i => i.campaign === campaign)[0].transferId = resp.data.transfer_id;
                setCompletedTransferList(newCompletedTransferList);
                console.log(`Transfer successful for campaign: ${selectedList[id]}`);
            } else if (resp.hasOwnProperty('error')) {
                newCompletedTransferList.filter(i => i.campaign === campaign)[0].transferred = false;
                let error_text = '';

                if (resp.error.hasOwnProperty("error")) error_text = resp.error.error
                else if (resp.error.hasOwnProperty("data") && resp.error.data.hasOwnProperty("text")) error_text = resp.error.data.text.message
                else if (resp.error.hasOwnProperty("data") && resp.error.data.hasOwnProperty("message")) error_text = resp.error.data.message
                else error_text = JSON.stringify(resp.error)

                if (resp.error.hasOwnProperty("status")) error_text = 'Error ' + resp.error.status + ': ' + error_text

                newCompletedTransferList.filter(i => i.campaign === campaign)[0].error = error_text;
                setCompletedTransferList(newCompletedTransferList);
                console.log('Error during transfer: ', resp)
            } else {
                newCompletedTransferList.filter(i => i.campaign === campaign)[0].transferred = false;
                newCompletedTransferList.filter(i => i.campaign === campaign)[0].error = 'unknown error';

                setCompletedTransferList(newCompletedTransferList);
                console.log('Error during transfer: ', transferResponse)
            }
        }
        // setSelectedList([])
    }

    const updateSearchParams = (paramOrUpdates, value) => {
        const updates = typeof paramOrUpdates === 'string'
            ? {[paramOrUpdates]: value}
            : paramOrUpdates;
        const params = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, nextValue]) => {
            params.set(key, nextValue == null ? '' : nextValue);
        });
        setSearchParams(params);
    }

    // URL обновляем с задержкой: каждый setSearchParams перерисовывает всё приложение
    // и добавляет запись в history (раньше — на каждую букву)
    const filterTextUrlTimer = useRef(null)
    useEffect(() => () => clearTimeout(filterTextUrlTimer.current), [])
    const handleFilterChange = (event) => {
        const value = event.target.value
        setFilterText(value)

        clearTimeout(filterTextUrlTimer.current)
        filterTextUrlTimer.current = setTimeout(() => {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev)
                params.set('filterText', value)
                return params
            })
        }, 300)
    }

    const handeFilterCampaignType = (event) => {
        setFilterCampaignType(event.target.value)
        updateSearchParams('filterCampaignType', event.target.value)
    }

    const handeFilterType = (value) => {
        const nextValue = value == null ? 'All' : value;
        setFilterType(nextValue)
        updateSearchParams('filterType', nextValue)
    }

    const handleChangeFilterStatus = (statuses) => {
        setFilterStatusesSelected(statuses);
        localStorage.setItem('filterStatuses', statuses.join(','))
        updateSearchParams('filterStatuses', statuses.join(','))

    }


    const formatdate = (timestamp) => {
        return formatInTimeZone(new Date(timestamp * 1000), 'America/New_York', 'dd.MM.yyyy HH:mm')
    }

    const chooseTransferDialogTitle = () => {

        if (errorAfterCheck || completedTransferList.some(i => (i.hasOwnProperty('transferred') && !i.transferred))) {
            return 'Some campaigns failed to be transferred'
        }
        if (completedTransferList.length === 0) {
            return 'Transfer starts soon...'
        }
        if (completedTransferList.every(i => i.transferred)
            && completedTransferList.length === transferInProgress.length
            && transferInProgress.every(i => i === false)) {

            return 'Transferred successfully:'
        }
        return 'In progress...'
    }

    const setEnv1Name = (evn1Name) => {
        setEnv1(evn1Name)
        const env2 = getEnvName('env2', sourceMode, searchParams);
        updateSearchParams({env: evn1Name, env2: env2});
        dispatch({
            type: 'EV1_NAME', payload: {
                data: {
                    env1Name: evn1Name
                }
            }
        });
    }

    const setEnv2Name = (evn2Name) => {
        setEnv2(evn2Name)
        const env1 = getEnvName('env', sourceMode, searchParams);
        updateSearchParams({env: env1, env2: evn2Name});
        dispatch({
            type: 'EV2_NAME', payload: {
                data: {
                    env2Name: evn2Name
                }
            }
        });
    }
    const swapEnv = () => {
        const env = getEnvName('env', sourceMode, searchParams);
        console.log("env")
        console.log(env)
        const env2Name = getEnvName('env2', sourceMode, searchParams);
        setEnv1Name(env2Name);
        setEnv2Name(env);
        updateSearchParams({env: env2Name, env2: env});

        setSelectedList([])
    }

    const activateCampaigns = (env) => {
        setShowConfirmActivate(true)
    }

    const upgradeCampaigns = (env) => {
        setShowConfirmUpgrade(true)
    }

    const bulkUpdate = (env) => {
        setBulkUpdateOpen(true)
    }

    const createDisabled = () => {
        if (environments === undefined) return false

        const envObj = getEnvByName(environments, env1)
        if (envObj !== undefined && envObj.length > 0) {
            const disabled = !envObj[0][1].hasCreator
            return disabled
        }

        return false
    }
    const createDisabledEnv2 = () => {
        if (environments === undefined) return false

        const envObj = getEnvByName(environments, env2)
        if (envObj !== undefined && envObj.length > 0) {
            const disabled = !envObj[0][1].hasCreator
            return disabled
        }

        return false
    }


    const sortByName = (a, b) => {
        return a[1].name.localeCompare(b[1].name);
    }

    const onCloseTransferDialog = () => {
        setSelectedList([])
        setErrorAfterCheck(false);
        setTransferInProgress([]);
        setOpenResultDialog(false);
        setCompletedTransferList([])
        setSelectedList([])
    }

    const handleCreateCampaingClick = () => {
        navigate(`/creator/campaigns?env=${env1}`, {
            state: createPublisherReturnState(location)
        })
    }

    const transferTooltipMsg = isProdEnv ? 'You are not allowed to transfer to prod' : `'${env1}' isn't contained in allowed sources of '${env2}'`;


    const sortedEnvirnoments = environments == undefined ? [] : Object.entries(environments).sort((a, b) => {
        if (a[1].reserved && !b[1].reserved) return -1;
        if (!a[1].reserved && b[1].reserved) return 1;
        return (b[1].reservedPriority - a[1].reservedPriority);
    })


    const hasLiveopsErrors = () => {
    }

    const getWarningsAlert = (item) => {

        if (cachedItems[item].item.warnings.length == 0) return ''
        return <Alert severity={"warning"}>{cachedItems[item].item.warnings.map(_warning => (
            <div>- {_warning}</div>))}</Alert>

    }

    const [newTransferPopup, setNewTransferPopup] = useState(false)

    return <>
        <Box>
            <Box
                className={"filters-sticky"}
                sx={{display: 'flex'}}
            >

                <Box sx={{pr: 1, width: "100px"}}>
                    <FormControl fullWidth>
                        <InputLabel id="super-type-id">Super Type</InputLabel>
                        <Select
                            size={"small"}
                            labelId="super-type"
                            id="super-type-select"
                            value={filterCampaignType}
                            label="Super Type"
                            onChange={handeFilterCampaignType}
                        >
                            <MenuItem value="All types">All</MenuItem>
                            <MenuItem value="LoadableContent">LC</MenuItem>
                            <MenuItem value="ExtendedContent">EC</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{pr: 1, width: "200px"}}>
                    <FormControl fullWidth>
                        {types != undefined && <Autocomplete
                            options={['All', ...Object.keys(types).sort()]}
                            size={"small"}

                            getOptionLabel={(option) => (
                                option + (SHORT_CAMPAIGN_NAMES.hasOwnProperty(option) ? ' (' + SHORT_CAMPAIGN_NAMES[option] + ')' : '')
                            )}

                            value={filterType ? filterType : null}


                            onChange={(event, value) =>
                                handeFilterType(value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    id="offerType"
                                    name="offerType"
                                    variant="outlined"
                                    label="Type"
                                />
                            )}

                            componentsProps={{popper: {style: {width: 'fit-content'}}}}
                            PopperProps={{
                                style: {
                                    minWidth: '1000px'
                                },
                                modifiers: [
                                    {
                                        name: 'computeStyles',
                                        options: {
                                            adaptive: false, // Disable adaptive to control width manually
                                        },
                                    },
                                ],
                            }}
                        />}
                    </FormControl>
                </Box>
                <Box sx={{pr: 1, width: "150px"}}>
                    <MultipleSelect
                        label={"Status"}
                        sx={{p: 0}}
                        labelId={"Statuses"}
                        items={statusItems}
                        defaultSelectedItems={filterStatusesSelected}
                        setSelectedItems={handleChangeFilterStatus}
                    />
                </Box>
                <Box sx={{flexGrow: 1, pr: 1,}}>
                    <TextField
                        size={"small"}
                        sx={{width: '100%'}}
                        value={filterText}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="campaign name or type"
                        onChange={(event) => handleFilterChange(event)}></TextField>
                </Box>
                <Box sx={{pr: 1, width: "170px"}}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="from"
                            value={dateFilter.from}
                            format={"y-M-d"}
                            slotProps={{ field: { size: 'small' } }}

                            onChange={(newValue) => {
                                setDateFilter({
                                    ...dateFilter,
                                    from: newValue
                                });
                                updateSearchParams('from', newValue.getTime() / 1000)
                                dispatch({
                                    type: 'DATE_FILTER_FROM', payload: {
                                        data: {
                                            from: newValue
                                        }
                                    }
                                });
                            }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{pr: 1, width: "170px"}}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="till"
                            format={"y-M-d"}
                            value={dateFilter.till}
                            slotProps={{ field: { size: 'small' } }}
                            onChange={(newValue) => {
                                setDateFilter({
                                    ...dateFilter,
                                    till: newValue
                                });
                                updateSearchParams('till', newValue.getTime() / 1000)
                                dispatch({
                                    type: 'DATE_FILTER_TILL', payload: {
                                        data: {
                                            till: newValue
                                        }
                                    }
                                });
                            }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </Box>
            </Box>


            <Box className="fix-top-transfer" sx={{display: 'flex', flexWrap: 'nowrap'}} spacing={0}
            >

                {!isProdEnv && <Box className="env-btn" alignSelf="center">
                    <Tooltip
                        disableHoverListener={!createDisabled()}
                        title={`'${env1}' disabled for creator`}>
                        <div>
                            <Button
                                size={"small"}
                                fullWidth
                                disabled={createDisabled()}
                                variant="contained"
                                color={"success"}
                                onClick={handleCreateCampaingClick}>
                                <AddIcon/>
                            </Button>
                        </div>
                    </Tooltip>
                </Box>}

                <Box sx={{pr: 0, pl: 1}} className="env-select" alignSelf="center">

                    {env2 === PROD && <div style={{textAlign: "right"}}><strong
                        style={{width: "100px", display: "inline-block"}}>{env1}</strong></div>}
                    {env2 !== PROD && <FormControl size="small" fullWidth>

                        {environments !== undefined &&

                            <Autocomplete
                                id="grouped-demo"
                                size={"small"}
                                style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}

                                options={sortedEnvirnoments}
                                groupBy={(option) => (option[1].reserved ? 'Reserved' : 'Custom')}
                                getOptionLabel={(option) => option[0]}
                                renderInput={(params) => <TextField {...params} label=""/>}
                                renderGroup={(params) => (
                                    <li key={params.key}>
                                        <ListSubheader>{params.group}</ListSubheader>
                                        {params.children}
                                    </li>
                                )}
                                onChange={(event, value) => {
                                    debugger
                                    if (value != null) {
                                        setSelectedList([])
                                        setEnv1Name(value[0])
                                    }
                                }}
                                value={sortedEnvirnoments.filter(item => item[0] == env1)[0]}

                                componentsProps={{popper: {style: {width: 'fit-content'}}}}
                                disableClearable={true}
                                PopperProps={{
                                    style: {
                                        minWidth: '1000px'
                                    },
                                    modifiers: [
                                        {
                                            name: 'computeStyles',
                                            options: {
                                                adaptive: false, // Disable adaptive to control width manually
                                            },
                                        },
                                    ],
                                }}
                            />

                        }

                    </FormControl>}

                </Box>
                <Box sx={{pr: 1, pl: 1, flex: "0 0 180px"}} alignSelf="center" textAlign="center"
                >

                    {(isAllowedSource && !(isProdEnv && !isAdmin(userSession))) ?
                        <Button
                            sx={{mt: 0}}

                            variant="contained" onClick={() => setShowConfirmTransfer(true)}
                            disabled={isAllowedSource === false || selectedList.length === 0}>
                            Transfer [{selectedList.length}]
                        </Button> :
                        <Tooltip sx={{mt: 0}}
                                 title={transferTooltipMsg}>
                            <Button variant="contained"
                                    color="secondary"
                                    fullWidth
                            >

                                Transfer [{selectedList.length}]
                            </Button>
                        </Tooltip>
                    }

                    {env2 == 'production' && <SyncPublisherProgress/>}

                </Box>
                <Box sx={{pr: 1, pl: 0}} className="env-select" alignSelf="center">
                    {env2 === PROD && <div style={{textAlign: "left"}}><strong
                        style={{width: "100px", display: "inline-block"}}>{env2}</strong></div>}
                    {env2 !== PROD && <FormControl size="small" fullWidth>
                        {environments !== undefined && <Autocomplete
                            id="grouped-demo"
                            size={"small"}
                            style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}

                            options={sortedEnvirnoments}
                            groupBy={(option) => (option[1].reserved ? 'Reserved' : 'Custom')}
                            getOptionLabel={(option) => option[0]}
                            renderInput={(params) => <TextField {...params} label=""/>}
                            renderGroup={(params) => (
                                <li key={params.key}>
                                    <ListSubheader>{params.group}</ListSubheader>
                                    {params.children}
                                </li>
                            )}
                            onChange={(event, value) => {
                                debugger
                                if (value != null) {
                                    setSelectedList([])
                                    setEnv2Name(value[0])

                                }
                            }}
                            value={sortedEnvirnoments.filter(item => item[0] == env2)[0]}

                            componentsProps={{popper: {style: {width: 'fit-content'}}}}
                            disableClearable={true}
                            PopperProps={{
                                style: {
                                    minWidth: '1000px'
                                },
                                modifiers: [
                                    {
                                        name: 'computeStyles',
                                        options: {
                                            adaptive: false, // Disable adaptive to control width manually
                                        },
                                    },
                                ],
                            }}
                        />}

                    </FormControl>}

                </Box>
                {
                    !isProdEnv &&
                    <Box className="env-btn" alignSelf="center">
                        <Tooltip
                            disableHoverListener={isAllowedForEnv(env2, env1)}
                            sx={{mt: 0}}
                            title={`'${env1}' isn't contained in allowed sources of '${env2}'`}>
                            <div>
                                <Button
                                    fullWidth
                                    size={"small"}
                                    variant="contained"
                                    color={"success"}
                                    disabled={!isAllowedForEnv(env2, env1)}
                                    onClick={() => swapEnv()}>
                                    <SyncAltIcon/>
                                </Button>
                            </div>
                        </Tooltip>
                    </Box>
                }

            </Box>

            {!calendarMode.active && <Grid container>
                <Grid item sm={12} md={6} sx={{pr: 1}}>
                    <CampaignFrame
                        externalLinks={externalLinks}
                        env={env1}
                        onSelectItem={onSelectItem}
                        selectedList={selectedList}
                        filterCampaignType={filterCampaignType === "All types" ? "" : filterCampaignType}
                        createDisabled={createDisabled()}
                        onSelectMultipleItems={onSelectMultipleItems}

                        filterStatuses={filterStatusesSelected}
                        dateFilter={dateFilter}
                        filterType={filterType}
                        filterText={filterText}
                        initialViewType={initialFilters.viewType}
                        activateCampaigns={activateCampaigns}
                        upgradeCampaigns={upgradeCampaigns}
                        bulkUpdate={bulkUpdate}
                        setCalendarMode={setCalendarMode}
                    />


                </Grid>

                <Grid sm={12} item md={6} sx={{pl: 1}}>
                    <CampaignFrame env={env2}
                                   externalLinks={externalLinks}
                                   createDisabled={createDisabledEnv2()}
                                   filterText={filterText}
                                   initialViewType={initialFilters.viewType}
                                   filterType={filterType}
                                   filterCampaignType={filterCampaignType === "All types" ? "" : filterCampaignType}
                                   filterStatuses={filterStatusesSelected}
                                   dateFilter={dateFilter}
                                   rightPanel={true}
                                   setCalendarMode={setCalendarMode}
                                   activateCampaigns={activateCampaigns}
                                   upgradeCampaigns={upgradeCampaigns}

                    />
                </Grid>

            </Grid>}
        </Box>

        {showConfirmActivate && <ConfirmMassActionDialog
            open={showConfirmActivate}
            title="You are going to Adjust Time for campaigns"
            selectedList={selectedList}
            cachedItems={cachedItems}
            setSelectedList={setSelectedList}
            onClose={() => setShowConfirmActivate(false)}
            onConfirm={() => {
                // Дополнительные действия после успешного выполнения
                setShowConfirmActivate(false);
            }}
            attribute={'id'}
            actionFunction={activateCampaign} // или любая другая функция
            environment={env1}
        />}


        {bulkUpdateOpen&&
            <AudienceBulkUpdateDialog
                open={bulkUpdateOpen}
                onClose={() => {setBulkUpdateOpen(false)}}
                selectedIds={selectedList || []}
                onSaved={() => {setBulkUpdateOpen(false); setSelectedList([]);}}
                items={cachedItems}
                environment={env1}

            />}

        {showConfirmUpgrade && <ConfirmMassActionDialog
            open={showConfirmUpgrade}
            title="You are going to Upgrade Bundles for campaigns"
            selectedList={selectedList}
            cachedItems={cachedItems}
            setSelectedList={setSelectedList}
            onClose={() => setShowConfirmUpgrade(false)}
            attribute={'bundleId'}
            onConfirm={() => {
                // Дополнительные действия после успешного выполнения
                setShowConfirmUpgrade(false);
            }}
            actionFunction={upgradeCampaign} // или любая другая функция
            environment={env1}
        />}



        {showConfirmTransfer && <PublishDiff
            env1={env1} env2={env2} campaigns={selectedList}
            showConfirmTransfer={showConfirmTransfer}
            setShowConfirmTransfer={() => {
                setShowConfirmTransfer(!showConfirmTransfer)
                setNewTransferPopup(false)
                setSkipBundleUpdateList([])
                setBundleUpdateInfo({})
            }}
            cachedItems={cachedItems}
            skipBundleUpdateList={skipBundleUpdateList}
            setSkipBundleUpdateList={setSkipBundleUpdateList}
            onBundleUpdateInfo={handleBundleUpdateInfo}
        >
            <Button variant={"outlined"} onClick={() => {
                setShowConfirmTransfer(!showConfirmTransfer)
                setNewTransferPopup(false)
            }}>Cancel</Button>
            {isProdAdmin(env2, userSession) &&
                <LoadingButton variant="contained" onClick={() => handleTransfer()} loading={transferResponse.isLoading}
                               disabled={selectedList.length == 0}>
                    Transfer
                </LoadingButton>}
        </PublishDiff>}

        {confirmErrorsDialog && <Dialog
            open={confirmErrorsDialog}
            onClose={() => {
                setConfirmErrorsDialog(!confirmErrorsDialog)
                setSelectedList([])
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="xl"
            fullWidth={true}
        >
            <DialogTitle id="alert-dialog-title">
                You are going to transfer campaigns with warnings from <b>{env1}</b> to <b>{env2}</b>. Are you sure?
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {!isProdAdmin(env2, userSession) ? <Alert severity={"error"}>Недостаточно прав</Alert> :
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Campaign name
                                    </TableCell>
                                    <TableCell>
                                        Warnings
                                    </TableCell>

                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {selectedList.filter(item => cachedItems[item].item.warnings.length > 0).map(item => (
                                    <TableRow selected={true}>
                                        <TableCell>{item}</TableCell>
                                        <TableCell>{getWarningsAlert(item)}</TableCell>
                                    </TableRow>

                                ))}
                                {selectedList.filter(item => cachedItems[item].item.warnings.length == 0).map(item => (
                                    <TableRow>
                                        <TableCell>{item}</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>

                                ))}
                            </TableBody>
                        </Table>}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant={"outlined"}
                        onClick={() => setConfirmErrorsDialog(!confirmErrorsDialog)}>Cancel</Button>
                {isProdAdmin(env2, userSession) &&
                    <LoadingButton variant="contained" onClick={() => handleTransfer(true)}
                                   loading={transferResponse.isLoading}
                                   disabled={selectedList.length == 0}>
                        Yes, i am
                    </LoadingButton>}
            </DialogActions>
        </Dialog>}

        <BundleUpdateConfirmDialog
            open={confirmBundleDialog}
            source={env1}
            target={env2}
            items={getBundleUpdateCampaignsList()}
            bundleUpdateInfo={bundleUpdateInfo}
            canConfirm={isProdAdmin(env2, userSession)}
            loading={transferResponse.isLoading}
            onCancel={() => setConfirmBundleDialog(false)}
            onConfirm={() => handleTransfer(true, true)}
        />

        {openResultDialog && <TransferResultDialog
            open={openResultDialog}
            onClose={onCloseTransferDialog}
            title={chooseTransferDialogTitle}
            transferResponse={transferResponse}
            completedTransferList={completedTransferList}
            env={env2}
            selectedList={selectedList}
            errorAfterCheck={errorAfterCheck}
            transferInProgress={transferInProgress}
            setTransferInProgress={setTransferInProgress}
            setErrorAfterCheck={setErrorAfterCheck}
        />}
    </>
}
